import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IrisIconComponent, IconSizes } from '@sondermind/iris/icon';
import { Logger } from '@sondermind/datadog-logging';
import { EngagementService } from '@sondermind/data-access/engagement';
import {
  IOnboardingTaskGroupInfo,
  IOnboardingTaskInfo,
  IOnboardingTaskStatusType,
} from '@sondermind/react/models';

type LoadState = 'loading' | 'loaded' | 'error';

@Component({
  selector: 'member-w9-task-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IrisIconComponent],
  styleUrls: ['./w9-task-list.component.scss'],
  templateUrl: './w9-task-list.component.html',
})
export class W9TaskListComponent implements OnInit {
  private readonly engagement = inject(EngagementService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);

  readonly iconSizes = IconSizes;
  readonly state = signal<LoadState>('loading');
  readonly visibleGroups = signal<IOnboardingTaskGroupInfo[]>([]);

  ngOnInit(): void {
    this.engagement
      .getOnboardingTaskList$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          // Note: HttpService.handleHttpError() maps 404 (and 401/403) to a null
          // response that completes successfully rather than erroring — so a
          // "provider not found" 404 lands here as null, not in the error branch.
          // An empty/null response renders the "You're all caught up." empty state.
          this.visibleGroups.set(this.toVisibleGroups(response?.taskGroups ?? []));
          this.state.set('loaded');
        },
        error: (err: unknown) => {
          this.logger.error('W9TaskList: failed to load onboarding tasks', { err });
          this.state.set('error');
        },
      });
  }

  isDone(task: IOnboardingTaskInfo): boolean {
    return task.status === IOnboardingTaskStatusType.COMPLETED;
  }

  isInProgress(task: IOnboardingTaskInfo): boolean {
    return task.status === IOnboardingTaskStatusType.IN_PROGRESS;
  }

  // Drop tasks the backend flags as inactive, then drop now-empty groups.
  private toVisibleGroups(groups: IOnboardingTaskGroupInfo[]): IOnboardingTaskGroupInfo[] {
    return groups
      .map((group) => ({ ...group, tasks: group.tasks.filter((task) => task.active !== false) }))
      .filter((group) => group.tasks.length > 0);
  }
}
